import { ReactNode, Suspense } from "react";

import { LeftSideMenu } from "@impero-com/common/components/leftSideMenu";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ApplicationBarToolbar,
    PageColumns,
    PageContent,
    PageLayout,
    PageSpinner,
} from "@impero-com/ui";
import { Outlet, useMatch, useParams } from "react-router-dom";

import { UserOverview } from "api/generated/model";
import { ErrorBoundary } from "components/errorBoundary";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { toUserOverview } from "utils/user";

import { OrgApplicationBarHeader } from "../../components/applicationBar/OrgApplicationBarHeader";
import { PageNavMenuLayout, PageNavMenuMain } from "../../components/layout";
import { WebRoute } from "../../routes";
import { useNewMenu } from "../navigation/useNewMenu";

import { useAssignmentCountQuery } from "./queries/useAssignmentCountQuery";
import { useUserWithDetailsQuery } from "./queries/useUserWithDetailsQuery";
import { createMenuItems, ID_USER_DETAILS, UserViewMenu } from "./sideMenu";
import {
    findUserSubpath,
    USER_ID_PATH,
    USER_VIEW_DEFAULT_TITLE,
} from "./userRoutes";

export interface OutletContext {
    user: UserOverview;
}

interface UserViewPageProps {
    actions?: ReactNode;
    /** The page. Defaults to the matched sub-page route. */
    children?: ReactNode;
}

/**
 * The app bar and the page navigation menu around a page of /users/:id.
 */
export function UserViewPage({ actions, children }: UserViewPageProps) {
    const { t } = useTranslation();
    const { id: userId } = useParams();
    const segment =
        useMatch(`${USER_ID_PATH}/:segment/*`)?.params.segment ?? "";

    if (!userId) {
        throw new Error("User ID parameter is missing in route");
    }

    const { data: assignmentCount } = useAssignmentCountQuery(userId);
    // Most of the sub-pages need the user details, and currently fetch it themselves
    // In the future, we could consider fetching it here and passing it down to the sub-pages to avoid duplicate requests.
    const { data: userWithDetails } = useUserWithDetailsQuery(Number(userId));
    const user = toUserOverview(userWithDetails.content.resource);

    const newMenu = useNewMenu();
    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const riskEntitiesEnabled = useFeatureFlag("riskEntities");

    // /users/:id/
    const isIndexRoute = segment === "";
    const subpath = findUserSubpath(segment);

    const usersBreadCrumb = {
        label: t("UserManagement_ModuleTitle"),
        url: WebRoute.USERS,
    };

    const title = isIndexRoute
        ? { title: formatUserName(user), breadCrumbs: [usersBreadCrumb] }
        : {
              title: t(subpath?.title ?? USER_VIEW_DEFAULT_TITLE),
              breadCrumbs: [
                  usersBreadCrumb,
                  {
                      label: formatUserName(user),
                      url: `${WebRoute.USERS}/${user.id}`,
                  },
              ],
          };

    /* itemId === segment, but the details page has no segment, so we use a constant for it. See sideMenu.tsx */
    const selectedItemId = isIndexRoute ? ID_USER_DETAILS : segment;

    const page = (
        <Suspense fallback={<PageSpinner />}>
            <ErrorBoundary routeKey={`${userId}/${segment}`}>
                {children ?? (
                    <Outlet context={{ user } satisfies OutletContext} />
                )}
            </ErrorBoundary>
        </Suspense>
    );

    if (newMenu) {
        return (
            <PageNavMenuLayout
                title={title}
                menu={
                    <UserViewMenu
                        userId={userId}
                        userName={formatUserName(user)}
                        segment={segment}
                        assignmentCount={assignmentCount}
                        controlTestingEnabled={controlTestingEnabled}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                        riskEntitiesEnabled={riskEntitiesEnabled}
                    />
                }
            >
                <PageNavMenuMain actions={actions}>{page}</PageNavMenuMain>
            </PageNavMenuLayout>
        );
    }

    return (
        <PageLayout fullHeight>
            <OrgApplicationBarHeader title={title}>
                <ApplicationBarToolbar
                    // An empty row on the pages without actions keeps the
                    // toolbar's height, so the content doesn't jump between pages.
                    actions={actions ?? <span />}
                />
            </OrgApplicationBarHeader>
            <PageColumns>
                <LeftSideMenu
                    items={createMenuItems({
                        assignmentCount,
                        userId,
                        controlTestingEnabled,
                        controlEntitiesEnabled,
                        riskEntitiesEnabled,
                    })}
                    selectedItemId={selectedItemId}
                    onItemSelected={() => {}}
                />
                <PageContent>{page}</PageContent>
            </PageColumns>
        </PageLayout>
    );
}
